import React, { useEffect, useState } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Stack,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  IconButton,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { CheckCircle, Cancel, CloudUpload } from '@mui/icons-material';
import { downloadDocument } from '../../services/CustomerService';
import { addDocument } from '../../services/ShareableApplicationService';

const REQUIRED_DOC_TYPES = ['CHEQUE', 'IDENTITY', 'ELECTRICITY BILL'];

const DocumentsList = ({ documents, decodedId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogUrl, setDialogUrl] = useState(null);
  const [dialogName, setDialogName] = useState('');
  const [dialogLoading, setDialogLoading] = useState(false);
  const isImage = /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(dialogUrl);

  useEffect(() => {
    // Reset loader when dialogUrl changes (for iframe)
    if (dialogUrl && !isImage) {
      setDialogLoading(true);
    }
  }, [dialogUrl, isImage]);

  const handleViewDialog = async (docId, docName) => {
    setDialogLoading(true);
    try {
      const data = await downloadDocument(docId);
      setDialogUrl(data);
      setDialogName(docName);
      setOpenDialog(true);
      setDialogLoading(false);
    } catch {
      setError('Failed to load document');
      setOpenDialog(false);
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDialogDownload = () => {
    if (dialogUrl) window.open(dialogUrl, '_blank');
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setDialogUrl(null);
    setDialogName('');
  };

  const handleUpload = async (event, docType) => {
    const file = event.target.files[0];
    if (!file) {
      setError('No file selected');
      return;
    }
    setLoading(true);
    try {
      await addDocument(file, decodedId, docType);
      setSuccess(true);
      alert(`${docType} uploaded successfully`);
    } catch {
      setError('Upload failed.');
    } finally {
      setLoading(false);
    }
  };

  const uploadedTypes = documents
    .map((doc) => doc?.document?.documentType)
    .filter(
      (type) =>
        type !== 'PANEL SERIAL NUMBER' &&
        type !== 'INVERTER SERIAL NUMBER' &&
        type !== 'CUSTOM DOCS',
    );

  const allTypes = Array.from(
    new Set([...REQUIRED_DOC_TYPES, ...uploadedTypes]),
  );

  const customDocs = documents.filter(
    (doc) => doc?.document?.documentType === 'CUSTOM DOCS',
  );

  return (
    <Stack spacing={2}>
      <List>
        {[
          ...allTypes,
          ...customDocs.map((doc) => doc?.document?.documentType),
        ].map((type, index) => {
          const doc = documents.find((d) => d?.document?.documentType === type);
          const isUploaded = !!doc?.document?.url;
          return (
            <ListItem
              key={index}
              sx={{
                borderBottom: '1px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                paddingLeft: 0,
                paddingRight: 0,
              }}
            >
              <Stack direction='row' alignItems='center' spacing={2}>
                <ListItemText
                  primary={
                    type === 'IDENTITY'
                      ? 'Aadhar copy'
                      : isUploaded
                        ? doc?.document?.name.charAt(0).toUpperCase() +
                          doc?.document?.name.slice(1).toLowerCase()
                        : type.charAt(0).toUpperCase() +
                          type.slice(1).toLowerCase()
                  }
                  secondary={
                    isUploaded
                      ? doc?.document?.description?.charAt(0).toUpperCase() +
                        doc?.document?.description?.slice(1).toLowerCase()
                      : 'Upload document'
                  }
                />
              </Stack>
              <Stack direction='row' spacing={1}>
                <ListItemIcon>
                  {isUploaded ? (
                    <CheckCircle color='success' />
                  ) : (
                    <Cancel color='error' />
                  )}
                </ListItemIcon>
                {isUploaded ? (
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={() =>
                      handleViewDialog(doc?.document?.id, doc?.document?.name)
                    }
                    sx={{ width: '80px' }}
                  >
                    View
                  </Button>
                ) : (
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<CloudUpload />}
                    component='label'
                    sx={{ width: '80px' }}
                  >
                    Add
                    <input
                      type='file'
                      hidden
                      onChange={(e) => handleUpload(e, type)}
                    />
                  </Button>
                )}
              </Stack>
            </ListItem>
          );
        })}
        {/* Custom Docs */}
        {customDocs.map((doc) => {
          const isUploaded = !!doc?.document?.url;
          return (
            <ListItem
              key={doc.document.id}
              sx={{
                borderBottom: '1px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                paddingLeft: 0,
                paddingRight: 0,
              }}
            >
              <Stack direction='row' alignItems='center' spacing={2}>
                <ListItemText
                  primary={
                    doc.document.name?.charAt(0).toUpperCase() +
                    doc.document.name?.slice(1).toLowerCase()
                  }
                  secondary={
                    isUploaded
                      ? doc.document.description?.charAt(0).toUpperCase() +
                        doc.document.description?.slice(1).toLowerCase()
                      : 'Upload document'
                  }
                />
              </Stack>
              <Stack direction='row' spacing={1}>
                <ListItemIcon>
                  {isUploaded ? (
                    <CheckCircle color='success' />
                  ) : (
                    <Cancel color='error' />
                  )}
                </ListItemIcon>
                {isUploaded ? (
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={() =>
                      handleViewDialog(doc.document.id, doc.document.name)
                    }
                    sx={{ width: '80px' }}
                  >
                    View
                  </Button>
                ) : null}
              </Stack>
            </ListItem>
          );
        })}
      </List>

      {loading && <CircularProgress />}
      {error && (
        <Snackbar open autoHideDuration={6000} onClose={() => setError('')}>
          <Alert severity='error' onClose={() => setError('')}>
            {error}
          </Alert>
        </Snackbar>
      )}
      {success && (
        <Snackbar
          open
          autoHideDuration={6000}
          onClose={() => setSuccess(false)}
        >
          <Alert severity='success' onClose={() => setSuccess(false)}>
            Document uploaded successfully!
          </Alert>
        </Snackbar>
      )}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth='md'
        fullWidth
      >
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'flex-end'}
        >
          <IconButton
            onClick={handleDialogDownload}
            sx={{ color: 'primary.main', background: 'white', mr: 1 }}
            aria-label='Download'
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            onClick={handleDialogClose}
            sx={{ color: 'primary.main', background: 'white', mr: 1 }}
            aria-label='Close'
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent sx={{ p: 4 }}>
          <Box
            width={'100%'}
            height={'100%'}
            display='flex'
            justifyContent='center'
            alignItems='center'
          >
            <Box sx={{ position: 'relative', width: '100%' }}>
              {dialogLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    zIndex: 2,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              <Box
                component={isImage ? 'img' : 'iframe'}
                src={
                  isImage
                    ? dialogUrl
                    : `https://docs.google.com/viewer?url=${encodeURIComponent(dialogUrl)}&embedded=true`
                }
                alt={dialogName}
                sx={{
                  height: isImage ? 'auto' : '80vh',
                  width: isImage ? '80vw' : '100%',
                  border: 'none',
                  borderRadius: 2,
                  cursor: isImage ? 'grab' : 'auto',
                  opacity: dialogLoading ? 0 : 1,
                  transition: 'opacity 0.2s',
                }}
                onClick={handleDialogDownload}
                onLoad={isImage ? undefined : () => setDialogLoading(false)}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Stack>
  );
};

export default DocumentsList;
